import {
  customerSchema,
  customerInputSchema,
  customerResponseSchema,
  customersResponseSchema,
  opticalQuoteResponseSchema,
  opticalQuotesResponseSchema,
  lensTintLevels,
  mirrorOperationalColors,
  prescriptionHistoryResponseSchema,
  progressiveSalesRules,
  quoteCreateSchema,
  salesFrameOptionResponseSchema,
  salesQuoteOptionsResponseSchema,
  uvOperationalColors,
  type Customer,
  type CustomerGender,
  type LensPriceCombination,
  type LensDesign,
  type OpticalPrescriptionInput,
  type OpticalQuote,
  type PrescriptionHistoryItem,
  type PrismDirection,
  type PrismMeasurementInput,
  type QuoteCreate,
  type SalesQuoteOptionsResponse,
  type SalesFrameOption,
  type SessionIdentity,
} from "@cosmo/contracts";
import { type FormEvent, useEffect, useMemo, useState } from "react";
import { OpticalSaleConversion } from "./OpticalSaleConversion";

const apiBaseUrl = (import.meta.env.VITE_API_BASE_URL || "").replace(/\/$/, "");

async function responseMessage(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { error?: { message?: string } };
    return body.error?.message || "No fue posible completar la solicitud.";
  } catch {
    return "No fue posible completar la solicitud.";
  }
}

function today(): string {
  const date = new Date();
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 10);
}

function futureDate(days: number): string {
  const date = new Date(`${today()}T12:00:00`);
  date.setDate(date.getDate() + days);
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 10);
}

function money(value: number | null): string {
  if (value === null) return "Precio pendiente";
  return new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(value);
}

type CustomerDraft = {
  names: string; paternalSurname: string; maternalSurname: string; birthDate: string;
  gender: CustomerGender | ""; phone: string; email: string; privacyAccepted: boolean;
  guardianName: string; guardianRelationship: string; guardianPhone: string;
};

type SearchDraft = {
  names: string; paternalSurname: string; maternalSurname: string; birthDate: string; phone: string;
};

type EyeDraft = {
  sphere: string; cylinder: string; axis: string; addition: string;
  dnp: string; baseCurve: string; height: string; corridor: string;
};

type PrismMeasurementDraft = { value: string; direction: "" | PrismDirection };
type PrismEyeDraft = { primary: PrismMeasurementDraft; secondary: PrismMeasurementDraft };

type QuoteDraft = {
  frameOrigin: "CATALOG" | "CUSTOMER";
  frameSku: string;
  productId: string;
  customerFrameBrand: string;
  customerFrameModel: string;
  customerFrameMaterial: string;
  customerFrameCondition: "Nuevo" | "Usado";
  customerFrameComments: string;
  customerFrameMeasureA: string;
  customerFrameMeasureB: string;
  customerFrameMeasureDbl: string;
  customerFrameMeasureEd: string;
  bevelType: "Biselado completo" | "Ranurado" | "Tres piezas";
  bevelFinish: "" | "Mate" | "Abrillantado" | "Facetado" | "Facetado+Abrillantado";
  lensCombinationId: string;
  prescriptionOrigin: "COSMO" | "EXTERNA";
  prescriberEmployeeNumber: string;
  externalConfirmed: boolean;
  od: EyeDraft;
  oi: EyeDraft;
  prismEnabled: boolean;
  prismOd: PrismEyeDraft;
  prismOi: PrismEyeDraft;
  rxSatisfied: "" | "YES" | "NO";
  rxNotSatisfiedComments: string;
  laboratoryComments: string;
  noPurchaseReason: "Compra en quincena" | "Necesita autorización" | "No tiene presupuesto" | "Otro";
  noPurchaseComments: string;
  followUpDate: string;
  estimatedDeliveryDate: string;
};

type LensSelectionDraft = {
  lensType: string;
  design: string;
  material: string;
  basePriceId: string;
  arPriceId: string;
  photoPriceId: string;
  photoColor: string;
  uvPriceId: string;
  uvColor: string;
  tintLevel: string;
};

type OperationChoice = "" | "QUOTE" | "SALE";

const NONE_OPTION = "__NONE__";

const blankLensSelection: LensSelectionDraft = {
  lensType: "",
  design: "",
  material: "",
  basePriceId: "",
  arPriceId: "",
  photoPriceId: "",
  photoColor: "",
  uvPriceId: "",
  uvColor: "",
  tintLevel: "",
};

const blankCustomer: CustomerDraft = {
  names: "", paternalSurname: "", maternalSurname: "", birthDate: "",
  gender: "", phone: "", email: "", privacyAccepted: false,
  guardianName: "", guardianRelationship: "", guardianPhone: "",
};

const blankSearch: SearchDraft = {
  names: "", paternalSurname: "", maternalSurname: "", birthDate: "", phone: "",
};

const blankEye = (): EyeDraft => ({
  sphere: "0.00", cylinder: "0.00", axis: "", addition: "", dnp: "32", baseCurve: "",
  height: "20", corridor: "",
});

const blankPrismMeasurement = (): PrismMeasurementDraft => ({ value: "", direction: "" });
const blankPrismEye = (): PrismEyeDraft => ({
  primary: blankPrismMeasurement(), secondary: blankPrismMeasurement(),
});

const blankQuote = (): QuoteDraft => ({
  frameOrigin: "CATALOG", frameSku: "", productId: "", customerFrameBrand: "",
  customerFrameModel: "", customerFrameMaterial: "", customerFrameCondition: "Nuevo",
  customerFrameComments: "", customerFrameMeasureA: "", customerFrameMeasureB: "",
  customerFrameMeasureDbl: "", customerFrameMeasureEd: "",
  bevelType: "Biselado completo", bevelFinish: "",
  lensCombinationId: "", prescriptionOrigin: "COSMO", prescriberEmployeeNumber: "", externalConfirmed: false,
  od: blankEye(), oi: blankEye(), prismEnabled: false, prismOd: blankPrismEye(), prismOi: blankPrismEye(),
  rxSatisfied: "", rxNotSatisfiedComments: "", laboratoryComments: "",
  noPurchaseReason: "Compra en quincena", noPurchaseComments: "",
  followUpDate: futureDate(7), estimatedDeliveryDate: futureDate(10),
});

function nullableNumber(value: string): number | null {
  return value.trim() === "" ? null : Number(value);
}

function numberInRange(value: string, minimum: number, maximum: number): boolean {
  if (value.trim() === "") return false;
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric >= minimum && numeric <= maximum;
}

function ageFromBirthDate(birthDate: string): number | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(birthDate)) return null;
  const [year = 0, month = 0, day = 0] = birthDate.split("-").map(Number);
  const current = new Date(`${today()}T12:00:00`);
  let age = current.getFullYear() - year;
  const currentMonth = current.getMonth() + 1;
  if (currentMonth < month || (currentMonth === month && current.getDate() < day)) age -= 1;
  return age >= 0 && age <= 130 ? age : null;
}

function eyePayload(draft: EyeDraft): OpticalPrescriptionInput["od"] {
  return {
    sphere: Number(draft.sphere),
    cylinder: Number(draft.cylinder),
    axis: nullableNumber(draft.axis),
    addition: nullableNumber(draft.addition),
    dnp: Number(draft.dnp),
    baseCurve: nullableNumber(draft.baseCurve),
    height: Number(draft.height),
    corridor: nullableNumber(draft.corridor),
  };
}

function prismMeasurementPayload(draft: PrismMeasurementDraft): PrismMeasurementInput | null {
  if (!draft.value && !draft.direction) return null;
  return { value: Number(draft.value), direction: draft.direction as PrismDirection };
}

function prismEyePayload(draft: PrismEyeDraft): NonNullable<OpticalPrescriptionInput["prismData"]["od"]> {
  return {
    primary: prismMeasurementPayload(draft.primary),
    secondary: prismMeasurementPayload(draft.secondary),
  };
}

function copyReusableEye(previous: OpticalPrescriptionInput["od"], current: EyeDraft): EyeDraft {
  return {
    ...current,
    sphere: previous.sphere.toFixed(2),
    cylinder: previous.cylinder.toFixed(2),
    axis: previous.axis === null ? "" : String(previous.axis),
    dnp: String(previous.dnp),
  };
}

function uniqueValues(values: string[]): string[] {
  return [...new Set(values)].sort((left, right) => left.localeCompare(right, "es"));
}

function optionId(value: string | null): string {
  return value ?? NONE_OPTION;
}

function optionLabel(value: string | null, label: string | null): string {
  return value === null ? "Ninguno" : label || "Opción disponible";
}

function requiresRangeRx(od: EyeDraft, oi: EyeDraft): boolean {
  return Math.abs(Number(od.cylinder) || 0) > 4 || Math.abs(Number(oi.cylinder) || 0) > 4;
}

function distinctOptions(
  combinations: LensPriceCombination[],
  idFor: (combination: LensPriceCombination) => string | null,
  labelFor: (combination: LensPriceCombination) => string | null,
): Array<{ id: string; label: string }> {
  const map = new Map<string, string>();
  combinations.forEach((combination) => {
    const rawId = idFor(combination);
    map.set(optionId(rawId), optionLabel(rawId, labelFor(combination)));
  });
  return [...map.entries()].map(([id, label]) => ({ id, label }));
}

function quarterValues(minimum: number, maximum: number): number[] {
  const values: number[] = [];
  for (let value = minimum; value <= maximum + 0.0001; value += 0.25) {
    values.push(Math.round(value * 100) / 100);
  }
  return values;
}

function signedRxLabel(value: number): string {
  return value > 0 ? `+${value.toFixed(2)}` : value.toFixed(2);
}

function progressiveRange(design: string, material: string) {
  const rule = progressiveSalesRules[design as LensDesign];
  if (!rule) return null;
  if (material === "CR-39") return rule.cr39;
  if (material === "Policarbonato") return rule.polycarbonate;
  return null;
}

function FrameMeasurementGuide() {
  return (
    <img
      alt="Guía original de Apps Script para medir A, B, DBL y ED del armazón"
      className="frame-guide-image"
      src="/medidas-armazon-appscript.png"
    />
  );
}

function PrescriptionCapture({ od, oi, disabled, lensType, design, material, onOdChange, onOiChange }: {
  od: EyeDraft; oi: EyeDraft; disabled: boolean; lensType: string; design: string; material: string;
  onOdChange: (value: EyeDraft) => void; onOiChange: (value: EyeDraft) => void;
}) {
  const needsAddition = lensType === "Progresivo" || lensType === "Bifocal";
  const needsCorridor = lensType === "Progresivo";
  const rule = progressiveSalesRules[design as LensDesign];
  const range = lensType === "Progresivo" ? progressiveRange(design, material) : null;
  const sphereValues = quarterValues(range?.sphereMin ?? -18, range?.sphereMax ?? 12);
  const cylinderValues = quarterValues(range?.cylinderMin ?? -7, range?.cylinderMax ?? 0);
  const additionValues = quarterValues(rule?.minAdd ?? 0.5, rule?.maxAdd ?? 4);
  const corridors = rule?.corridors ?? [];
  const eyeRow = (code: "OD" | "OI", label: string, value: EyeDraft, onChange: (next: EyeDraft) => void) => {
    const field = (key: keyof EyeDraft, next: string) => onChange({ ...value, [key]: next });
    return (
      <tr key={code}>
        <th className="rx-eye-heading" scope="row"><strong>{code}</strong><small>{label}</small></th>
        <td data-label="Esfera"><select aria-label={`Esfera ${label}`} className={Number(value.sphere) < 0 ? "rx-negative" : "rx-positive"} disabled={disabled} onChange={(e) => field("sphere", e.target.value)} required value={Number(value.sphere).toFixed(2)}>{sphereValues.map((item) => <option key={item} style={{ color: item < 0 ? "#b42318" : "#102a43" }} value={item.toFixed(2)}>{signedRxLabel(item)}</option>)}</select></td>
        <td data-label="Cilindro"><select aria-label={`Cilindro ${label}`} className={Number(value.cylinder) < 0 ? "rx-negative" : "rx-positive"} disabled={disabled} onChange={(e) => field("cylinder", e.target.value)} required value={Number(value.cylinder).toFixed(2)}>{cylinderValues.map((item) => <option key={item} style={{ color: item < 0 ? "#b42318" : "#102a43" }} value={item.toFixed(2)}>{signedRxLabel(item)}</option>)}</select></td>
        <td data-label="Eje"><input aria-label={`Eje ${label}`} disabled={disabled || Number(value.cylinder) === 0} max="180" min="0" onChange={(e) => field("axis", e.target.value)} step="1" type="number" value={Number(value.cylinder) === 0 ? "" : value.axis} /></td>
        <td data-label="Adición"><select aria-label={`Adición ${label}`} disabled={disabled || !needsAddition} onChange={(e) => field("addition", e.target.value)} required={needsAddition} value={needsAddition ? value.addition : ""}><option value="">{needsAddition ? "Selecciona" : "No aplica"}</option>{additionValues.map((item) => <option key={item} value={item.toFixed(2)}>{signedRxLabel(item)}</option>)}</select></td>
        <td data-label="DNP"><input aria-label={`DNP ${label}`} disabled={disabled} max="45" min="20" onChange={(e) => field("dnp", e.target.value)} required step="0.5" type="number" value={value.dnp} /></td>
        <td data-label="Curva base"><select aria-label={`Curva base ${label}`} disabled={disabled} onChange={(e) => field("baseCurve", e.target.value)} value={value.baseCurve}><option value="">No aplica</option>{[1, 2, 3, 4, 5, 6, 7, 8, 9].map((item) => <option key={item}>{item}</option>)}</select></td>
        <td data-label="Altura"><input aria-label={`Altura ${label}`} disabled={disabled} max="45" min="10" onChange={(e) => field("height", e.target.value)} required step="0.5" type="number" value={value.height} /></td>
        <td data-label="Corredor"><select aria-label={`Corredor ${label}`} disabled={disabled || !needsCorridor || corridors.length === 0} onChange={(e) => field("corridor", e.target.value)} required={needsCorridor} value={needsCorridor ? value.corridor : ""}><option value="">{needsCorridor ? "Selecciona" : "No aplica"}</option>{corridors.map((item) => <option key={item} value={String(item)}>{item} mm</option>)}</select></td>
      </tr>
    );
  };
  return (
    <div className="rx-table-wrap">
      <table className="rx-capture-table">
        <thead><tr><th>Ojo</th><th>Esfera</th><th>Cilindro</th><th>Eje</th><th>ADD</th><th>DNP</th><th>CB</th><th>Altura</th><th>Corredor</th></tr></thead>
        <tbody>{eyeRow("OD", "Ojo derecho", od, onOdChange)}{eyeRow("OI", "Ojo izquierdo", oi, onOiChange)}</tbody>
      </table>
      <small className="rx-range-help">La esfera, cilindro y adición avanzan en pasos de 0.25.{range ? ` Rango vigente para ${material}: esfera ${signedRxLabel(range.sphereMin)} a ${signedRxLabel(range.sphereMax)} y cilindro ${signedRxLabel(range.cylinderMin)} a 0.00.` : ""} El corredor sólo se habilita cuando corresponde.</small>
    </div>
  );
}

const prismDirections: Array<{ value: PrismDirection; label: string }> = [
  { value: "IN", label: "Adentro" }, { value: "OUT", label: "Afuera" },
  { value: "UP", label: "Arriba" }, { value: "DOWN", label: "Abajo" },
];

function PrismEyeFields({ label, value, disabled, onChange }: {
  label: string; value: PrismEyeDraft; disabled: boolean; onChange: (value: PrismEyeDraft) => void;
}) {
  const field = (measurement: keyof PrismEyeDraft, key: keyof PrismMeasurementDraft, next: string) => {
    onChange({ ...value, [measurement]: { ...value[measurement], [key]: next } });
  };
  return (
    <fieldset className="prism-eye-card">
      <legend>{label}</legend>
      {(["primary", "secondary"] as const).map((measurement, index) => <div className="prism-row" key={measurement}>
        <label>{index === 0 ? "Prisma principal" : "Segundo prisma opcional"}<input disabled={disabled} max="20" min="0.25" onChange={(e) => field(measurement, "value", e.target.value)} required={index === 0} step="0.25" type="number" value={value[measurement].value} /></label>
        <label>Dirección<select disabled={disabled} onChange={(e) => field(measurement, "direction", e.target.value)} required={index === 0 || Boolean(value[measurement].value)} value={value[measurement].direction}><option value="">Selecciona</option>{prismDirections.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select></label>
      </div>)}
    </fieldset>
  );
}

export function SalesOperations({ session, onClose }: { session: SessionIdentity; onClose: () => void }) {
  const [options, setOptions] = useState<SalesQuoteOptionsResponse["data"] | null>(null);
  const [quotes, setQuotes] = useState<OpticalQuote[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [search, setSearch] = useState<SearchDraft>(blankSearch);
  const [customer, setCustomer] = useState<CustomerDraft>(blankCustomer);
  const [quote, setQuote] = useState<QuoteDraft>(blankQuote);
  const [lensSelection, setLensSelection] = useState<LensSelectionDraft>(blankLensSelection);
  const [activeStep, setActiveStep] = useState<1 | 2 | 3>(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [createdQuote, setCreatedQuote] = useState<OpticalQuote | null>(null);
  const [conversionQuote, setConversionQuote] = useState<OpticalQuote | null>(null);
  const [operationChoice, setOperationChoice] = useState<OperationChoice>("");
  const [previousPrescriptions, setPreviousPrescriptions] = useState<PrescriptionHistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [draftReady, setDraftReady] = useState(false);
  const [lookedUpFrame, setLookedUpFrame] = useState<SalesFrameOption | null>(null);
  const [frameSearching, setFrameSearching] = useState(false);
  const [frameLookupMessage, setFrameLookupMessage] = useState("");
  const canWrite = session.permissions.includes("SALES_WRITE");
  const draftKey = `cosmo:sales-draft:${session.tenantId}:${session.userId}:${session.branchId || "none"}`;

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(draftKey);
      if (raw) {
        const saved = JSON.parse(raw) as {
          version?: number; quote?: QuoteDraft; customer?: CustomerDraft; lensSelection?: LensSelectionDraft;
          selectedCustomer?: unknown; activeStep?: 1 | 2 | 3; operationChoice?: OperationChoice;
        };
        if (saved.version === 1) {
          if (saved.quote) setQuote({ ...blankQuote(), ...saved.quote });
          if (saved.customer) setCustomer({ ...blankCustomer, ...saved.customer });
          if (saved.lensSelection) setLensSelection({ ...blankLensSelection, ...saved.lensSelection });
          const restoredCustomer = customerSchema.safeParse(saved.selectedCustomer);
          if (restoredCustomer.success) setSelectedCustomer(restoredCustomer.data);
          if (saved.activeStep && saved.activeStep >= 1 && saved.activeStep <= 3) setActiveStep(saved.activeStep);
          if (saved.operationChoice === "QUOTE" || saved.operationChoice === "SALE") setOperationChoice(saved.operationChoice);
          setMessage("Se recuperó la captura pendiente de esta sesión.");
        }
      }
    } catch {
      sessionStorage.removeItem(draftKey);
    } finally {
      setDraftReady(true);
    }
  }, [draftKey]);

  useEffect(() => {
    if (!draftReady || !canWrite) return;
    sessionStorage.setItem(draftKey, JSON.stringify({
      version: 1, quote, customer, lensSelection, selectedCustomer, activeStep, operationChoice,
    }));
  }, [activeStep, canWrite, customer, draftKey, draftReady, lensSelection, operationChoice, quote, selectedCustomer]);

  useEffect(() => {
    if (!conversionQuote) return;
    window.setTimeout(() => document.getElementById("optical-sale-payment")?.scrollIntoView({ behavior: "smooth", block: "start" }), 0);
  }, [conversionQuote]);

  async function loadQuotes() {
    const response = await fetch(`${apiBaseUrl}/api/v1/sales/quotes`, { credentials: "include", headers: { Accept: "application/json" } });
    if (!response.ok) throw new Error(await responseMessage(response));
    setQuotes(opticalQuotesResponseSchema.parse(await response.json()).data);
  }

  useEffect(() => {
    let active = true;
    const refreshToken = Date.now().toString();
    const freshRequest: RequestInit = {
      credentials: "include",
      cache: "no-store",
      headers: { Accept: "application/json", "Cache-Control": "no-cache" },
    };
    Promise.all([
      fetch(`${apiBaseUrl}/api/v1/sales/quote-options?refresh=${refreshToken}`, freshRequest),
      fetch(`${apiBaseUrl}/api/v1/sales/quotes?refresh=${refreshToken}`, freshRequest),
    ]).then(async ([optionsResponse, quotesResponse]) => {
      if (!optionsResponse.ok) throw new Error(await responseMessage(optionsResponse));
      if (!quotesResponse.ok) throw new Error(await responseMessage(quotesResponse));
      const nextOptions = salesQuoteOptionsResponseSchema.parse(await optionsResponse.json()).data;
      const nextQuotes = opticalQuotesResponseSchema.parse(await quotesResponse.json()).data;
      if (!active) return;
      setOptions(nextOptions);
      setQuotes(nextQuotes);
      setQuote((current) => ({
        ...current,
        productId: current.productId,
        lensCombinationId: "",
      }));
    }).catch((cause: unknown) => {
      if (active) setMessage(cause instanceof Error ? cause.message : "No fue posible preparar las cotizaciones.");
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const selectedFrame = lookedUpFrame?.productId === quote.productId
    ? lookedUpFrame
    : options?.stock.find((item) => item.productId === quote.productId);
  const customerAge = ageFromBirthDate(customer.birthDate);
  const customerIsMinor = customerAge !== null && customerAge < 18;
  const rangeRxRequired = requiresRangeRx(quote.od, quote.oi);
  const rangeCompatible = useMemo(
    () => (options?.combinations || []).filter((item) => item.appliesRangeRx === rangeRxRequired),
    [options, rangeRxRequired],
  );
  const lensTypes = useMemo(() => uniqueValues(rangeCompatible.map((item) => item.lensType)), [rangeCompatible]);
  const byType = useMemo(
    () => rangeCompatible.filter((item) => item.lensType === lensSelection.lensType),
    [rangeCompatible, lensSelection.lensType],
  );
  const designs = useMemo(() => uniqueValues(byType.map((item) => item.design)), [byType]);
  const byDesign = useMemo(
    () => byType.filter((item) => item.design === lensSelection.design),
    [byType, lensSelection.design],
  );
  const materials = useMemo(() => uniqueValues(byDesign.map((item) => item.material)), [byDesign]);
  const byMaterial = useMemo(
    () => byDesign.filter((item) => item.material === lensSelection.material),
    [byDesign, lensSelection.material],
  );
  const baseOptions = useMemo(
    () => distinctOptions(byMaterial, (item) => item.basePriceId, (item) => item.baseLabel),
    [byMaterial],
  );
  const byBase = useMemo(
    () => byMaterial.filter((item) => item.basePriceId === lensSelection.basePriceId),
    [byMaterial, lensSelection.basePriceId],
  );
  const arOptions = useMemo(
    () => distinctOptions(byBase, (item) => item.arPriceId, (item) => item.arLabel),
    [byBase],
  );
  const byAr = useMemo(
    () => byBase.filter((item) => optionId(item.arPriceId) === lensSelection.arPriceId),
    [byBase, lensSelection.arPriceId],
  );
  const photoOptions = useMemo(
    () => distinctOptions(byAr, (item) => item.photoPriceId, (item) => item.photoLabel),
    [byAr],
  );
  const byPhoto = useMemo(
    () => byAr.filter((item) => optionId(item.photoPriceId) === lensSelection.photoPriceId),
    [byAr, lensSelection.photoPriceId],
  );
  const photoColors = useMemo(
    () => uniqueValues(byPhoto.map((item) => item.photoColor || "").filter(Boolean)),
    [byPhoto],
  );
  const byPhotoColor = useMemo(
    () => byPhoto.filter((item) => item.photoPriceId === null || item.photoColor === lensSelection.photoColor),
    [byPhoto, lensSelection.photoColor],
  );
  const uvOptions = useMemo(
    () => distinctOptions(byPhotoColor, (item) => item.uvPriceId, (item) => item.uvLabel),
    [byPhotoColor],
  );
  const compatibleFinals = useMemo(
    () => byPhotoColor.filter((item) => optionId(item.uvPriceId) === lensSelection.uvPriceId),
    [byPhotoColor, lensSelection.uvPriceId],
  );
  const selectedLens = compatibleFinals.length === 1 ? compatibleFinals[0] : undefined;
  const selectedUvLabel = uvOptions.find((item) => item.id === lensSelection.uvPriceId)?.label || null;
  const requiresOperationalColor = selectedUvLabel === "Color UV" || selectedUvLabel === "Espejeado";
  const requiresTintLevel = selectedUvLabel === "Color UV";
  const operationalColors = selectedUvLabel === "Espejeado" ? mirrorOperationalColors : uvOperationalColors;
  const availableTintLevels = lensSelection.material === "Policarbonato"
    ? lensTintLevels.slice(0, 2)
    : lensTintLevels;
  const lensOperationalReady = Boolean(
    !requiresOperationalColor
    || (lensSelection.uvColor
      && (!requiresTintLevel || availableTintLevels.includes(lensSelection.tintLevel as typeof lensTintLevels[number]))),
  );
  const selectedLensReady = Boolean(
    selectedLens?.readyForSale && selectedLens.totalPrice !== null && lensOperationalReady,
  );
  const customerFrameMeasurementsComplete = numberInRange(quote.customerFrameMeasureA, 38, 71)
    && numberInRange(quote.customerFrameMeasureB, 22, 51)
    && numberInRange(quote.customerFrameMeasureDbl, 10, 21)
    && numberInRange(quote.customerFrameMeasureEd, 39, 71);
  const selectedFrameReady = quote.frameOrigin === "CATALOG"
    ? Boolean(selectedFrame)
    : Boolean(
      quote.customerFrameBrand.trim() && quote.customerFrameModel.trim()
      && quote.customerFrameMaterial.trim() && customerFrameMeasurementsComplete,
    );
  const activeProgressiveRule = progressiveSalesRules[lensSelection.design as LensDesign];
  const framePrice = quote.frameOrigin === "CATALOG" ? selectedFrame?.publicPrice || 0 : options?.customerFrameServicePrice || 0;
  const total = framePrice + (selectedLens?.totalPrice || 0);
  const subtotal = Math.round((total / 1.16) * 100) / 100;
  const vat = Math.round((total - subtotal) * 100) / 100;
  const prismPrimaryComplete = !quote.prismEnabled || Boolean(
    quote.prismOd.primary.value && quote.prismOd.primary.direction
    && quote.prismOi.primary.value && quote.prismOi.primary.direction,
  );
  const bevelComplete = quote.bevelType === "Biselado completo" || Boolean(quote.bevelFinish);
  const eyeMissingFields = (eye: EyeDraft): string[] => {
    const missing: string[] = [];
    if (Math.abs(Number(eye.cylinder)) > 0.0001 && !numberInRange(eye.axis, 0, 180)) missing.push("eje");
    if ((lensSelection.lensType === "Progresivo" || lensSelection.lensType === "Bifocal") && !eye.addition) missing.push("adición");
    if (!numberInRange(eye.dnp, 20, 45)) missing.push("DNP");
    if (!numberInRange(eye.height, 10, 45)) missing.push("altura");
    if (lensSelection.lensType === "Progresivo" && !eye.corridor) missing.push("corredor");
    return missing;
  };
  const continuationBlockers: string[] = [];
  if (!canWrite) continuationBlockers.push("Tu perfil no tiene permiso para registrar cotizaciones.");
  if (!selectedFrameReady) {
    continuationBlockers.push(quote.frameOrigin === "CATALOG"
      ? "Captura o escanea el SKU y valida el armazón encontrado."
      : "Completa marca, modelo, material y las medidas A, B, DBL y ED del armazón del cliente.");
  }
  if (!bevelComplete) continuationBlockers.push("Selecciona el acabado del bisel.");
  if (!selectedLens) {
    continuationBlockers.push("Completa la configuración de lentes hasta obtener una ficha compatible.");
  } else if (!lensOperationalReady) {
    continuationBlockers.push("Completa el color y el nivel de tinte requeridos para los lentes.");
  } else if (!selectedLens.readyForSale || selectedLens.totalPrice === null) {
    continuationBlockers.push(`Completa los precios pendientes: ${selectedLens.missingPriceComponents.join("; ")}.`);
  }
  if (selectedLens) {
    const odMissing = eyeMissingFields(quote.od);
    const oiMissing = eyeMissingFields(quote.oi);
    if (odMissing.length > 0) continuationBlockers.push(`Completa en ojo derecho: ${odMissing.join(", ")}.`);
    if (oiMissing.length > 0) continuationBlockers.push(`Completa en ojo izquierdo: ${oiMissing.join(", ")}.`);
  }
  if (!prismPrimaryComplete) continuationBlockers.push("Completa el prisma principal y su dirección en ambos ojos.");
  if (!quote.rxSatisfied) continuationBlockers.push("Indica si el cliente está conforme con la graduación.");
  if (quote.rxSatisfied === "NO" && quote.rxNotSatisfiedComments.trim().length < 3) {
    continuationBlockers.push("Describe el motivo de inconformidad del cliente.");
  }
  if (quote.prescriptionOrigin === "COSMO" && !quote.prescriberEmployeeNumber) {
    continuationBlockers.push("Selecciona al optometrista que prescribió la receta.");
  }
  if (quote.prescriptionOrigin === "EXTERNA" && !quote.externalConfirmed) {
    continuationBlockers.push("Confirma que la receta externa fue revisada y quedará resguardada.");
  }
  const canContinueToBudget = continuationBlockers.length === 0;

  useEffect(() => {
    const nextId = selectedLens?.id || "";
    setQuote((current) => current.lensCombinationId === nextId ? current : { ...current, lensCombinationId: nextId });
  }, [selectedLens?.id]);

  useEffect(() => {
    if (lensSelection.lensType === "Visión sencilla") {
      setQuote((current) => ({
        ...current,
        od: { ...current.od, addition: "", corridor: "" },
        oi: { ...current.oi, addition: "", corridor: "" },
      }));
    } else if (lensSelection.lensType === "Bifocal") {
      setQuote((current) => ({
        ...current,
        od: { ...current.od, corridor: "" },
        oi: { ...current.oi, corridor: "" },
      }));
    }
  }, [lensSelection.lensType]);

  function changeLensSelection(key: keyof LensSelectionDraft, value: string) {
    const order: Array<keyof LensSelectionDraft> = [
      "lensType", "design", "material", "basePriceId", "arPriceId", "photoPriceId", "photoColor", "uvPriceId",
      "uvColor", "tintLevel",
    ];
    const index = order.indexOf(key);
    setLensSelection((current) => {
      const next = { ...current, [key]: value };
      order.slice(index + 1).forEach((field) => { next[field] = ""; });
      return next;
    });
  }

  async function searchCustomers(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setMessage("");
    const params = new URLSearchParams(search);
    const response = await fetch(`${apiBaseUrl}/api/v1/sales/customers?${params}`, { credentials: "include", headers: { Accept: "application/json" } });
    if (!response.ok) { setMessage(await responseMessage(response)); return; }
    const result = customersResponseSchema.parse(await response.json()).data;
    setCustomers(result);
    if (result.length === 0) setMessage("No se encontró un cliente con esos datos. Puedes registrarlo.");
  }

  async function createCustomer(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const payload = {
      ...customer,
      gender: customer.gender || null,
      email: customer.email.trim() || null,
      names: customer.names.trim(),
      paternalSurname: customer.paternalSurname.trim(),
      maternalSurname: customer.maternalSurname.trim(),
      phone: customer.phone.trim(),
      guardianName: customerIsMinor ? customer.guardianName.trim() : null,
      guardianRelationship: customerIsMinor ? customer.guardianRelationship.trim() : null,
      guardianPhone: customerIsMinor ? customer.guardianPhone.trim() : null,
    };
    const parsed = customerInputSchema.safeParse(payload);
    if (!parsed.success) { setMessage("Completa los datos obligatorios y acepta el aviso de privacidad."); return; }
    setSaving(true); setMessage("");
    try {
      const response = await fetch(`${apiBaseUrl}/api/v1/sales/customers`, {
        method: "POST", credentials: "include",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify(parsed.data),
      });
      if (!response.ok) throw new Error(await responseMessage(response));
      const saved = customerResponseSchema.parse(await response.json()).data;
      setSelectedCustomer(saved); setCustomers([saved]); setCustomer(blankCustomer);
      setMessage("Cliente registrado y seleccionado para la cotización.");
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : "No fue posible registrar el cliente.");
    } finally { setSaving(false); }
  }

  async function loadPreviousPrescriptions() {
    if (!selectedCustomer) return;
    setHistoryLoading(true); setMessage("");
    try {
      const response = await fetch(`${apiBaseUrl}/api/v1/sales/customers/${selectedCustomer.id}/prescriptions`, {
        credentials: "include", headers: { Accept: "application/json" },
      });
      if (!response.ok) throw new Error(await responseMessage(response));
      const result = prescriptionHistoryResponseSchema.parse(await response.json()).data;
      setPreviousPrescriptions(result);
      if (result.length === 0) setMessage("El cliente no tiene recetas anteriores registradas.");
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : "No fue posible consultar las recetas anteriores.");
    } finally { setHistoryLoading(false); }
  }

  async function findFrameBySku() {
    const sku = quote.frameSku.trim();
    setLookedUpFrame(null);
    setQuote((current) => ({ ...current, frameSku: sku, productId: "" }));
    if (!/^\d{6}$/.test(sku)) {
      setFrameLookupMessage("Captura o escanea un SKU de seis dígitos.");
      return;
    }
    setFrameSearching(true);
    setFrameLookupMessage("Buscando el SKU en el inventario de esta sucursal…");
    try {
      const response = await fetch(`${apiBaseUrl}/api/v1/sales/frame-options/${encodeURIComponent(sku)}`, {
        credentials: "include",
        cache: "no-store",
        headers: { Accept: "application/json", "Cache-Control": "no-cache" },
      });
      if (!response.ok) throw new Error(await responseMessage(response));
      const found = salesFrameOptionResponseSchema.parse(await response.json()).data;
      setLookedUpFrame(found);
      setQuote((current) => ({ ...current, frameSku: found.sku, productId: found.productId }));
      setFrameLookupMessage("Armazón encontrado. Compara la ficha con la etiqueta y la pieza física.");
    } catch (cause) {
      setFrameLookupMessage(cause instanceof Error ? cause.message : "No fue posible consultar el SKU.");
    } finally {
      setFrameSearching(false);
    }
  }

  function applyPreviousPrescription(item: PrescriptionHistoryItem) {
    setQuote((current) => ({
      ...current,
      od: copyReusableEye(item.prescription.od, current.od),
      oi: copyReusableEye(item.prescription.oi, current.oi),
    }));
    setMessage("Se copiaron esfera, cilindro, eje y DNP. Revisa adición, altura, corredor y curva base para la orden actual.");
  }

  async function createQuote(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!operationChoice) { setMessage("Selecciona si la operación será Venta o Cotización."); return; }
    if (!selectedCustomer) { setMessage("Selecciona o registra primero al cliente."); return; }
    if (!selectedFrameReady) {
      setMessage(quote.frameOrigin === "CATALOG"
        ? "Captura o escanea el SKU y valida el armazón encontrado."
        : "Captura la información y las cuatro medidas del armazón del cliente.");
      return;
    }
    if (!selectedLensReady) {
      setMessage(lensOperationalReady
        ? "La combinación puede consultarse, pero no puede cotizarse hasta completar sus precios de venta."
        : "Completa el color y el nivel de tinte requeridos para esta configuración.");
      return;
    }
    const frame: QuoteCreate["frame"] = quote.frameOrigin === "CATALOG"
      ? { origin: "CATALOG", productId: quote.productId, brand: null, model: null, material: null, condition: null, comments: null }
      : {
        origin: "CUSTOMER", productId: null,
        brand: quote.customerFrameBrand.trim(), model: quote.customerFrameModel.trim(),
        material: quote.customerFrameMaterial.trim(), condition: quote.customerFrameCondition,
        comments: quote.customerFrameComments.trim() || null,
        measurements: {
          measureA: Number(quote.customerFrameMeasureA),
          measureB: Number(quote.customerFrameMeasureB),
          measureDbl: Number(quote.customerFrameMeasureDbl),
          measureEd: Number(quote.customerFrameMeasureEd),
        },
      };
    const payload: QuoteCreate = {
      customerId: selectedCustomer.id,
      frame,
      bevelType: quote.bevelType,
      bevelFinish: quote.bevelType === "Biselado completo" ? null : quote.bevelFinish || null,
      lensCombinationId: quote.lensCombinationId,
      lensSpecifications: {
        uvColor: requiresOperationalColor ? lensSelection.uvColor as QuoteCreate["lensSpecifications"]["uvColor"] : null,
        tintLevel: requiresTintLevel ? lensSelection.tintLevel as QuoteCreate["lensSpecifications"]["tintLevel"] : null,
      },
      prescription: {
        origin: quote.prescriptionOrigin,
        externalConfirmed: quote.prescriptionOrigin === "EXTERNA" && quote.externalConfirmed,
        prescriberEmployeeNumber: quote.prescriptionOrigin === "COSMO" ? quote.prescriberEmployeeNumber : null,
        od: eyePayload(quote.od), oi: eyePayload(quote.oi),
        prismData: quote.prismEnabled
          ? { enabled: true, od: prismEyePayload(quote.prismOd), oi: prismEyePayload(quote.prismOi) }
          : { enabled: false, od: null, oi: null },
        rxSatisfied: quote.rxSatisfied || null,
        rxNotSatisfiedComments: quote.rxSatisfied === "NO" ? quote.rxNotSatisfiedComments.trim() || null : null,
        laboratoryComments: quote.laboratoryComments.trim() || null,
      },
      noPurchaseReason: operationChoice === "SALE" ? "Otro" : quote.noPurchaseReason,
      noPurchaseComments: operationChoice === "SALE" ? "Venta directa pendiente de pago" : quote.noPurchaseComments.trim() || null,
      followUpDate: operationChoice === "SALE" ? today() : quote.followUpDate,
      estimatedDeliveryDate: quote.estimatedDeliveryDate,
    };
    const parsed = quoteCreateSchema.safeParse(payload);
    if (!parsed.success) { setMessage(parsed.error.issues[0]?.message || "Revisa todos los datos de la cotización."); return; }
    setSaving(true); setMessage("");
    try {
      const response = await fetch(`${apiBaseUrl}/api/v1/sales/quotes`, {
        method: "POST", credentials: "include",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify(parsed.data),
      });
      if (!response.ok) throw new Error(await responseMessage(response));
      const saved = opticalQuoteResponseSchema.parse(await response.json()).data;
      if (operationChoice === "SALE") {
        setCreatedQuote(null);
        setConversionQuote(saved);
        setMessage(`Ficha ${saved.folio} protegida. Completa el pago y la entrega para registrar la venta.`);
      } else {
        setCreatedQuote(saved);
        setMessage(`Cotización ${saved.folio} guardada sin cobro ni movimiento de inventario.`);
      }
      sessionStorage.removeItem(draftKey);
      await loadQuotes();
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : "No fue posible guardar la cotización.");
    } finally { setSaving(false); }
  }

  if (loading) return <section className="loading-panel"><span className="loading-ring" aria-hidden="true" /><h1>Cargando venta graduada</h1><p>Preparando clientes, fichas y precios vigentes…</p></section>;

  return (
    <section className="admin-shell sales-shell" aria-labelledby="sales-title">
      <div className="section-heading"><div><p className="eyebrow">Etapa 5 de 10 · Venta graduada</p><h1 id="sales-title">Presupuesto, cotización y venta óptica</h1><p>El flujo conserva la forma de trabajo vigente: cotiza con las fichas centrales y convierte la cotización protegida al recibir el pago.</p></div><button className="secondary-button" onClick={onClose}>Volver al inicio</button></div>
      {message && <p className="form-message info-message" aria-live="polite">{message}</p>}

      <nav className="sales-progress" aria-label="Avance de la cotización">
        <button className={activeStep === 1 ? "active" : ""} onClick={() => setActiveStep(1)} type="button"><span>1</span>Cliente</button>
        <button className={activeStep === 2 ? "active" : ""} disabled={!selectedCustomer} onClick={() => setActiveStep(2)} type="button"><span>2</span>Producto y receta</button>
        <button className={activeStep === 3 ? "active" : ""} disabled={!selectedCustomer || !canContinueToBudget} onClick={() => setActiveStep(3)} type="button"><span>3</span>Presupuesto</button>
      </nav>

      <div hidden={activeStep !== 1}>
      <div className="sales-step"><span>1</span><div><h2>Selecciona al cliente</h2><p>Busca con al menos dos datos o registra un cliente nuevo.</p></div></div>
      <div className="sales-customer-grid">
        <form className="sales-card" onSubmit={searchCustomers}>
          <h3>Buscar cliente</h3>
          <div className="field-grid"><label>Nombre(s)<input onChange={(e) => setSearch({ ...search, names: e.target.value })} value={search.names} /></label><label>Apellido paterno<input onChange={(e) => setSearch({ ...search, paternalSurname: e.target.value })} value={search.paternalSurname} /></label><label>Fecha de nacimiento<input onChange={(e) => setSearch({ ...search, birthDate: e.target.value })} type="date" value={search.birthDate} /></label><label>Teléfono<input inputMode="numeric" maxLength={10} onChange={(e) => setSearch({ ...search, phone: e.target.value.replace(/\D/g, "") })} value={search.phone} /></label></div>
          <button className="secondary-button">Buscar cliente</button>
          <div className="customer-results">{customers.map((item) => <button className={selectedCustomer?.id === item.id ? "customer-option selected" : "customer-option"} key={item.id} onClick={() => setSelectedCustomer(item)} type="button"><strong>{item.fullName}</strong><span>{item.birthDate} · {item.phone}</span></button>)}</div>
        </form>
        {canWrite && <form className="sales-card" onSubmit={createCustomer}>
          <h3>Registrar cliente</h3>
          <div className="field-grid"><label>Nombre(s)<input disabled={saving} onChange={(e) => setCustomer({ ...customer, names: e.target.value })} required value={customer.names} /></label><label>Apellido paterno<input disabled={saving} onChange={(e) => setCustomer({ ...customer, paternalSurname: e.target.value })} required value={customer.paternalSurname} /></label><label>Apellido materno<input disabled={saving} onChange={(e) => setCustomer({ ...customer, maternalSurname: e.target.value })} required value={customer.maternalSurname} /></label><label>Fecha de nacimiento<input disabled={saving} max={today()} onChange={(e) => setCustomer({ ...customer, birthDate: e.target.value, guardianName: "", guardianRelationship: "", guardianPhone: "" })} required type="date" value={customer.birthDate} />{customerAge !== null && <small className="field-help">Edad calculada: {customerAge} años</small>}</label><label>Teléfono a 10 dígitos<input disabled={saving} inputMode="numeric" maxLength={10} onChange={(e) => setCustomer({ ...customer, phone: e.target.value.replace(/\D/g, "") })} required value={customer.phone} /></label><label>Correo opcional<input disabled={saving} onChange={(e) => setCustomer({ ...customer, email: e.target.value })} type="email" value={customer.email} /></label><label>Género<select disabled={saving} onChange={(e) => setCustomer({ ...customer, gender: e.target.value as CustomerDraft["gender"] })} value={customer.gender}><option value="">No capturado</option><option>Masculino</option><option>Femenino</option><option>Prefiere no responder</option></select></label></div>
          {customerIsMinor && <fieldset className="guardian-card"><legend>Tutor obligatorio para cliente menor de edad</legend><p>El menor debe estar acompañado por una persona adulta responsable.</p><div className="field-grid"><label>Nombre completo del tutor<input disabled={saving} onChange={(e) => setCustomer({ ...customer, guardianName: e.target.value })} required value={customer.guardianName} /></label><label>Parentesco<input disabled={saving} onChange={(e) => setCustomer({ ...customer, guardianRelationship: e.target.value })} required value={customer.guardianRelationship} /></label><label>Teléfono del tutor<input disabled={saving} inputMode="numeric" maxLength={10} onChange={(e) => setCustomer({ ...customer, guardianPhone: e.target.value.replace(/\D/g, "") })} required value={customer.guardianPhone} /></label></div></fieldset>}
          <label className="check-line"><input checked={customer.privacyAccepted} disabled={saving} onChange={(e) => setCustomer({ ...customer, privacyAccepted: e.target.checked })} type="checkbox" />El cliente acepta el aviso de privacidad y el tratamiento de sus datos.</label>
          <button className="primary-button" disabled={saving}>Guardar y seleccionar</button>
        </form>}
      </div>
      {selectedCustomer && <div className="step-actions"><button className="primary-button" onClick={() => setActiveStep(2)} type="button">Continuar a producto y receta</button></div>}
      </div>

      {selectedCustomer && <div className="selected-customer"><span>Cliente seleccionado</span><strong>{selectedCustomer.fullName}</strong><small>{selectedCustomer.age} años · {selectedCustomer.phone}</small></div>}

      <form onSubmit={createQuote}>
        <div hidden={activeStep !== 2}>
        <div className="sales-step"><span>2</span><div><h2>Armazón, lentes y graduación</h2><p>Se utilizan las mismas fichas y precios centrales del catálogo.</p></div></div>
        <section className="sales-card quote-card">
          <div className="field-grid">
            <label>Origen del armazón<select disabled={!canWrite || saving} onChange={(e) => { setLookedUpFrame(null); setFrameLookupMessage(""); setQuote({ ...quote, frameOrigin: e.target.value as QuoteDraft["frameOrigin"], frameSku: "", productId: "" }); }} value={quote.frameOrigin}><option value="CATALOG">Catálogo de la sucursal</option><option value="CUSTOMER">Armazón del cliente</option></select></label>
            {quote.frameOrigin === "CATALOG" ? <section className="frame-sku-panel wide-field" aria-labelledby="frame-sku-title">
              <div className="frame-sku-heading"><div><h3 id="frame-sku-title">Buscar armazón por SKU</h3><p>Escribe el SKU o escanea el código de barras y presiona Enter.</p></div><span>Manual o lector</span></div>
              <div className="frame-sku-entry">
                <label>SKU del armazón<input autoComplete="off" disabled={!canWrite || saving || frameSearching} inputMode="numeric" maxLength={6} onChange={(e) => { const frameSku = e.target.value.replace(/\D/g, ""); const keepsSelection = selectedFrame?.sku === frameSku; setQuote({ ...quote, frameSku, productId: keepsSelection ? quote.productId : "" }); if (!keepsSelection) { setLookedUpFrame(null); setFrameLookupMessage(""); } }} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void findFrameBySku(); } }} placeholder="Ej. 100001" value={quote.frameSku} /></label>
                <button className="primary-button" disabled={!canWrite || saving || frameSearching} onClick={() => void findFrameBySku()} type="button">{frameSearching ? "Buscando…" : "Buscar"}</button>
              </div>
              {frameLookupMessage && <p aria-live="polite" className={selectedFrame ? "frame-lookup-message success" : "frame-lookup-message"}>{frameLookupMessage}</p>}
              {selectedFrame && <div className="frame-validation-card">
                <div className="frame-validation-heading"><div><span>Armazón validado</span><h3>{selectedFrame.description}</h3></div><strong>{money(selectedFrame.publicPrice)}</strong></div>
                <p>Compara estos datos con la etiqueta y con el armazón físico antes de continuar.</p>
                <dl className="frame-validation-details">
                  <div><dt>SKU</dt><dd>{selectedFrame.sku}</dd></div>
                  <div><dt>Marca</dt><dd>{selectedFrame.brand}</dd></div>
                  <div><dt>Código de parte</dt><dd>{selectedFrame.partNumber}</dd></div>
                  <div><dt>Material</dt><dd>{selectedFrame.material}</dd></div>
                  <div><dt>Color</dt><dd>{selectedFrame.color}</dd></div>
                  <div><dt>Existencia</dt><dd>{selectedFrame.quantity} {selectedFrame.quantity === 1 ? "pieza" : "piezas"}</dd></div>
                </dl>
                <div className="frame-validation-measures"><span>A <b>{selectedFrame.measureA} mm</b></span><span>B <b>{selectedFrame.measureB} mm</b></span><span>DBL <b>{selectedFrame.measureDbl} mm</b></span><span>ED <b>{selectedFrame.measureEd} mm</b></span></div>
              </div>}
            </section> : <><label>Marca<input disabled={!canWrite || saving} onChange={(e) => setQuote({ ...quote, customerFrameBrand: e.target.value })} required value={quote.customerFrameBrand} /></label><label>Modelo<input disabled={!canWrite || saving} onChange={(e) => setQuote({ ...quote, customerFrameModel: e.target.value })} required value={quote.customerFrameModel} /></label><label>Material<input disabled={!canWrite || saving} onChange={(e) => setQuote({ ...quote, customerFrameMaterial: e.target.value })} required value={quote.customerFrameMaterial} /></label><label>Condición<select disabled={!canWrite || saving} onChange={(e) => setQuote({ ...quote, customerFrameCondition: e.target.value as "Nuevo" | "Usado" })} value={quote.customerFrameCondition}><option>Nuevo</option><option>Usado</option></select></label><label className="wide-field">Estado visible y observaciones del armazón<textarea disabled={!canWrite || saving} maxLength={1000} onChange={(e) => setQuote({ ...quote, customerFrameComments: e.target.value })} placeholder="Describe rayones, piezas flojas, desgaste u otra condición visible." value={quote.customerFrameComments} /></label></>}
            <label>Tipo de bisel<select disabled={!canWrite || saving} onChange={(e) => setQuote({ ...quote, bevelType: e.target.value as QuoteDraft["bevelType"], bevelFinish: e.target.value === "Biselado completo" ? "" : quote.bevelFinish })} value={quote.bevelType}><option>Biselado completo</option><option>Ranurado</option><option>Tres piezas</option></select></label>
            {quote.bevelType !== "Biselado completo" && <label>Acabado<select disabled={!canWrite || saving} onChange={(e) => setQuote({ ...quote, bevelFinish: e.target.value as QuoteDraft["bevelFinish"] })} required value={quote.bevelFinish}><option value="">Selecciona</option><option>Mate</option><option>Abrillantado</option><option>Facetado</option><option>Facetado+Abrillantado</option></select></label>}
            <label>Origen de receta<select disabled={!canWrite || saving} onChange={(e) => setQuote({ ...quote, prescriptionOrigin: e.target.value as "COSMO" | "EXTERNA", prescriberEmployeeNumber: "", externalConfirmed: false })} value={quote.prescriptionOrigin}><option value="COSMO">Examen COSMO</option><option value="EXTERNA">Receta externa</option></select></label>
            {quote.prescriptionOrigin === "COSMO" && <label>Optometrista que prescribe<select disabled={!canWrite || saving} onChange={(e) => setQuote({ ...quote, prescriberEmployeeNumber: e.target.value })} required value={quote.prescriberEmployeeNumber}><option value="">Selecciona número de empleado</option>{options?.prescribers.map((item) => <option key={item.userId} value={item.employeeNumber}>{item.employeeNumber} · {item.displayName}</option>)}</select>{options?.prescribers.length === 0 && <small className="field-help error-text">No hay optometristas activos asignados a esta sucursal.</small>}</label>}
          </div>
          {quote.frameOrigin === "CUSTOMER" && <section className="frame-measurement-card" aria-label="Medidas del armazón del cliente"><div><h3>Medidas del armazón del cliente</h3><p>Captura las cuatro medidas del armazón que entrega el cliente.</p><div className="frame-measurement-inputs"><label>Medida A (38–71 mm)<input disabled={!canWrite || saving} max="71" min="38" onChange={(e) => setQuote({ ...quote, customerFrameMeasureA: e.target.value })} required step="0.1" type="number" value={quote.customerFrameMeasureA} /></label><label>Medida B (22–51 mm)<input disabled={!canWrite || saving} max="51" min="22" onChange={(e) => setQuote({ ...quote, customerFrameMeasureB: e.target.value })} required step="0.1" type="number" value={quote.customerFrameMeasureB} /></label><label>DBL (10–21 mm)<input disabled={!canWrite || saving} max="21" min="10" onChange={(e) => setQuote({ ...quote, customerFrameMeasureDbl: e.target.value })} required step="0.1" type="number" value={quote.customerFrameMeasureDbl} /></label><label>ED (39–71 mm)<input disabled={!canWrite || saving} max="71" min="39" onChange={(e) => setQuote({ ...quote, customerFrameMeasureEd: e.target.value })} required step="0.1" type="number" value={quote.customerFrameMeasureEd} /></label></div><small>Estas medidas se guardarán con la cotización y acompañarán la orden de laboratorio.</small></div><FrameMeasurementGuide /></section>}
          {quote.prescriptionOrigin === "EXTERNA" && <label className="check-line"><input checked={quote.externalConfirmed} disabled={!canWrite || saving} onChange={(e) => setQuote({ ...quote, externalConfirmed: e.target.checked })} type="checkbox" />Confirmo que la receta externa fue revisada y quedará resguardada.</label>}

          <section className="lens-builder" aria-labelledby="lens-builder-title">
            <div><h3 id="lens-builder-title">Configuración de lentes</h3><p>Selecciona cada característica en orden. Se muestran las combinaciones activas; las que aún no tienen precio pueden consultarse, pero no cotizarse.</p></div>
            <div className="lens-builder-grid">
              <label>Tipo de lente<select disabled={!canWrite || saving} onChange={(e) => changeLensSelection("lensType", e.target.value)} required value={lensSelection.lensType}><option value="">Selecciona</option>{lensTypes.map((item) => <option key={item}>{item}</option>)}</select></label>
              <label>Diseño<select disabled={!lensSelection.lensType || !canWrite || saving} onChange={(e) => changeLensSelection("design", e.target.value)} required value={lensSelection.design}><option value="">Selecciona</option>{designs.map((item) => <option key={item}>{item}</option>)}</select></label>
              <label>Material<select disabled={!lensSelection.design || !canWrite || saving} onChange={(e) => changeLensSelection("material", e.target.value)} required value={lensSelection.material}><option value="">Selecciona</option>{materials.map((item) => <option key={item}>{item}</option>)}</select></label>
              <label>Lente base<select disabled={!lensSelection.material || !canWrite || saving} onChange={(e) => changeLensSelection("basePriceId", e.target.value)} required value={lensSelection.basePriceId}><option value="">Selecciona</option>{baseOptions.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}</select></label>
              <label>Antirreflejante<select disabled={!lensSelection.basePriceId || !canWrite || saving} onChange={(e) => changeLensSelection("arPriceId", e.target.value)} required value={lensSelection.arPriceId}><option value="">Selecciona</option>{arOptions.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}</select></label>
              <label>Fotosensible<select disabled={!lensSelection.arPriceId || !canWrite || saving} onChange={(e) => changeLensSelection("photoPriceId", e.target.value)} required value={lensSelection.photoPriceId}><option value="">Selecciona</option>{photoOptions.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}</select></label>
              {lensSelection.photoPriceId && lensSelection.photoPriceId !== NONE_OPTION && <label>Color fotosensible<select disabled={!canWrite || saving} onChange={(e) => changeLensSelection("photoColor", e.target.value)} required value={lensSelection.photoColor}><option value="">Selecciona</option>{photoColors.map((item) => <option key={item}>{item}</option>)}</select></label>}
              <label>Protección UV<select disabled={!lensSelection.photoPriceId || (lensSelection.photoPriceId !== NONE_OPTION && !lensSelection.photoColor) || !canWrite || saving} onChange={(e) => changeLensSelection("uvPriceId", e.target.value)} required value={lensSelection.uvPriceId}><option value="">Selecciona</option>{uvOptions.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}</select></label>
              {requiresOperationalColor && <label>{selectedUvLabel === "Espejeado" ? "Color de espejeado" : "Color UV"}<select disabled={!canWrite || saving} onChange={(e) => changeLensSelection("uvColor", e.target.value)} required value={lensSelection.uvColor}><option value="">Selecciona color</option>{operationalColors.map((item) => <option key={item}>{item}</option>)}</select></label>}
              {requiresTintLevel && <label>Nivel de tinte<select disabled={!lensSelection.uvColor || !canWrite || saving} onChange={(e) => changeLensSelection("tintLevel", e.target.value)} required value={lensSelection.tintLevel}><option value="">Selecciona nivel</option>{availableTintLevels.map((item) => <option key={item} value={item}>{item.replace("Nivel ", "")}</option>)}</select>{lensSelection.material === "Policarbonato" && <small className="field-help">El policarbonato sólo permite nivel 0.5 o nivel 1.</small>}</label>}
            </div>
            {rangeRxRequired && <p className="lens-notice warning">La graduación requiere rango RX especial por cilindro mayor a 4.00. El cargo compatible se aplica automáticamente.</p>}
            {(lensSelection.material === "Alto índice" || lensSelection.material === "1.74") && <p className="lens-notice">Material de alto índice: verifica medidas, montaje y compatibilidad del armazón antes de continuar.</p>}
            {activeProgressiveRule && <aside className="lens-rule-card" aria-label={`Reglas de ${lensSelection.design}`}>
              <strong>Reglas de la ficha · {lensSelection.design}</strong>
              <span>ADD {activeProgressiveRule.minAdd.toFixed(2)} a {activeProgressiveRule.maxAdd.toFixed(2)}</span>
              <span>Corredor {activeProgressiveRule.corridors.join(" o ")} mm</span>
              <span>{activeProgressiveRule.minHeight === null ? "Sin altura mínima especial" : `Altura mínima ${activeProgressiveRule.minHeight} mm`}</span>
              <span>{activeProgressiveRule.maxCombined === null ? "Sin límite combinado especial" : `Poder combinado máximo ${activeProgressiveRule.maxCombined} D`}</span>
            </aside>}
            {selectedLens ? <><div className="lens-result"><span>{selectedLensReady ? "Ficha compatible seleccionada" : "Combinación disponible para consulta"} · {selectedLens.combinationCode}</span><strong>{selectedLens.baseLabel}{selectedLens.arLabel ? ` · ${selectedLens.arLabel}` : ""}{selectedLens.photoLabel ? ` · ${selectedLens.photoLabel} ${selectedLens.photoColor || ""}` : ""}{selectedLens.uvLabel ? ` · ${selectedLens.uvLabel}` : ""}{lensSelection.uvColor ? ` · ${lensSelection.uvColor}` : ""}{lensSelection.tintLevel ? ` · ${lensSelection.tintLevel}` : ""}</strong><b>{money(selectedLens.totalPrice)}</b></div><p className="lens-price-formula">Cálculo automático: {selectedLens.priceBreakdown.map((component) => `${component.label} ${money(component.salePrice)}`).join(" + ")}{selectedLens.readyForSale ? ` = ${money(selectedLens.totalPrice)}` : ""}</p></> : lensSelection.uvPriceId && <p className="lens-notice warning">No existe una ficha activa que reúna exactamente estas características. Revisa la selección.</p>}
            {selectedLens && !lensOperationalReady && <p className="lens-notice warning">Completa el color y el nivel requerido para continuar.</p>}
            {selectedLens && lensOperationalReady && !selectedLensReady && <p className="lens-notice warning">Precio pendiente. Falta completar: {selectedLens.missingPriceComponents.join("; ")}. El presupuesto y la venta permanecerán bloqueados.</p>}
          </section>
          <section className="prescription-section" aria-labelledby="prescription-title">
            <div className="prescription-heading"><div><h3 id="prescription-title">Graduación</h3><p>Los valores positivos se muestran en negro y los negativos en rojo. El rango se ajusta al diseño y material seleccionados.</p></div><button className="secondary-button" disabled={!selectedCustomer || historyLoading} onClick={() => void loadPreviousPrescriptions()} type="button">{historyLoading ? "Consultando…" : "Buscar recetas anteriores"}</button></div>
            {previousPrescriptions.length > 0 && <div className="previous-rx-list" aria-label="Recetas anteriores del cliente">{previousPrescriptions.map((item) => <article key={item.id}><div><strong>{new Date(item.createdAt).toLocaleDateString("es-MX")}</strong><span>{item.prescription.origin === "COSMO" ? `Examen COSMO${item.prescriberName ? ` · ${item.prescriberName}` : ""}` : "Receta externa"}</span></div><button className="secondary-button" onClick={() => applyPreviousPrescription(item)} type="button">Usar datos ópticos</button></article>)}</div>}
            <PrescriptionCapture design={lensSelection.design} disabled={!canWrite || saving || !lensSelection.material} lensType={lensSelection.lensType} material={lensSelection.material} od={quote.od} oi={quote.oi} onOdChange={(od) => setQuote({ ...quote, od })} onOiChange={(oi) => setQuote({ ...quote, oi })} />
          </section>

          <section className="prism-section" aria-labelledby="prism-title">
            <label className="check-line"><input checked={quote.prismEnabled} disabled={!canWrite || saving} onChange={(e) => setQuote({ ...quote, prismEnabled: e.target.checked, prismOd: e.target.checked ? quote.prismOd : blankPrismEye(), prismOi: e.target.checked ? quote.prismOi : blankPrismEye() })} type="checkbox" /><span><strong id="prism-title">Agregar prismas a la orden</strong><small>Captura el prisma principal de ambos ojos. El segundo es opcional.</small></span></label>
            {quote.prismEnabled && <div className="prism-grid"><PrismEyeFields disabled={!canWrite || saving} label="Ojo derecho (OD)" onChange={(prismOd) => setQuote({ ...quote, prismOd })} value={quote.prismOd} /><PrismEyeFields disabled={!canWrite || saving} label="Ojo izquierdo (OI)" onChange={(prismOi) => setQuote({ ...quote, prismOi })} value={quote.prismOi} /></div>}
          </section>

          <section className="rx-confirmation-card" aria-labelledby="rx-confirmation-title">
            <div><h3 id="rx-confirmation-title">Confirmación del cliente</h3><p>Registra la respuesta antes de guardar la cotización.</p></div>
            <label>¿El cliente está conforme con la graduación?<select disabled={!canWrite || saving} onChange={(e) => setQuote({ ...quote, rxSatisfied: e.target.value as QuoteDraft["rxSatisfied"], rxNotSatisfiedComments: e.target.value === "NO" ? quote.rxNotSatisfiedComments : "" })} required value={quote.rxSatisfied}><option value="">Selecciona</option><option value="YES">Sí</option><option value="NO">No</option></select></label>
            {quote.rxSatisfied === "NO" && <label>Motivo de inconformidad<textarea disabled={!canWrite || saving} maxLength={1000} minLength={3} onChange={(e) => setQuote({ ...quote, rxNotSatisfiedComments: e.target.value })} required value={quote.rxNotSatisfiedComments} /></label>}
          </section>

          <label>Comentarios para laboratorio<textarea disabled={!canWrite || saving} maxLength={2500} onChange={(e) => setQuote({ ...quote, laboratoryComments: e.target.value })} value={quote.laboratoryComments} /></label>
          {continuationBlockers.length > 0 && <aside className="continuation-blockers" aria-live="polite" role="status"><strong>No puedes continuar todavía:</strong><ul>{continuationBlockers.map((blocker) => <li key={blocker}>{blocker}</li>)}</ul></aside>}
          <div className="step-actions"><button className="secondary-button" onClick={() => setActiveStep(1)} type="button">Volver al cliente</button><button className="primary-button" disabled={!canContinueToBudget} onClick={() => setActiveStep(3)} type="button">Continuar al presupuesto</button></div>
        </section>
        </div>

        <div hidden={activeStep !== 3}>
        <div className="sales-step"><span>3</span><div><h2>Venta o cotización</h2><p>Selecciona qué operación desea realizar el cliente antes de continuar.</p></div></div>
        <section className="sales-card quote-card">
          <section className="operation-choice-card" aria-labelledby="operation-choice-title">
            <div><h3 id="operation-choice-title">¿Qué deseas registrar?</h3><p>Esta decisión es obligatoria y determina el siguiente paso.</p></div>
            <label>Tipo de operación *<select disabled={!canWrite || saving} onChange={(e) => setOperationChoice(e.target.value as OperationChoice)} required value={operationChoice}><option value="">Selecciona Venta o Cotización</option><option value="SALE">Venta</option><option value="QUOTE">Cotización</option></select></label>
            {operationChoice === "SALE" && <p className="operation-choice-note sale-note"><strong>Venta:</strong> se protegerá la ficha y se abrirá Pago y entrega para registrar el cobro.</p>}
            {operationChoice === "QUOTE" && <p className="operation-choice-note quote-note"><strong>Cotización:</strong> se guardará para seguimiento, sin cobro ni movimiento de inventario.</p>}
          </section>
          {operationChoice === "QUOTE" && <div className="field-grid"><label>Motivo de seguimiento<select disabled={!canWrite || saving} onChange={(e) => setQuote({ ...quote, noPurchaseReason: e.target.value as QuoteDraft["noPurchaseReason"] })} value={quote.noPurchaseReason}><option>Compra en quincena</option><option>Necesita autorización</option><option>No tiene presupuesto</option><option>Otro</option></select></label><label>Fecha de seguimiento<input disabled={!canWrite || saving} min={today()} onChange={(e) => setQuote({ ...quote, followUpDate: e.target.value })} required type="date" value={quote.followUpDate} /></label>{quote.noPurchaseReason === "Otro" && <label>Detalle<input disabled={!canWrite || saving} onChange={(e) => setQuote({ ...quote, noPurchaseComments: e.target.value })} required value={quote.noPurchaseComments} /></label>}</div>}
          {operationChoice !== "" && <div className="field-grid"><label>Entrega estimada<input disabled={!canWrite || saving} min={today()} onChange={(e) => setQuote({ ...quote, estimatedDeliveryDate: e.target.value })} required type="date" value={quote.estimatedDeliveryDate} /></label></div>}
          <div className="quote-summary"><div><span>Armazón o servicio</span><strong>{money(framePrice)}</strong></div><div><span>Lentes</span><strong>{money(selectedLens?.totalPrice || 0)}</strong></div><div><span>Subtotal</span><strong>{money(subtotal)}</strong></div><div><span>IVA incluido</span><strong>{money(vat)}</strong></div><div className="quote-total"><span>Total cotizado</span><strong>{money(total)}</strong></div></div>
          {!operationChoice && canWrite && <p className="operation-blocker" aria-live="polite">Selecciona Venta o Cotización para continuar.</p>}
          <div className="step-actions"><button className="secondary-button" onClick={() => setActiveStep(2)} type="button">Volver a producto y receta</button>{canWrite ? <button className="primary-button" disabled={saving || !operationChoice || !selectedCustomer || !selectedLens || total <= 0}>{saving ? "Guardando ficha…" : operationChoice === "SALE" ? "Continuar a pago y entrega" : operationChoice === "QUOTE" ? "Guardar cotización" : "Selecciona una operación"}</button> : <p className="form-message info-message">Tu perfil puede consultar, pero no registrar operaciones.</p>}</div>
        </section>
        </div>
      </form>

      {createdQuote && <section className="quote-success"><span>Cotización guardada</span><strong>{createdQuote.folio}</strong><p>Vigente hasta {createdQuote.validUntil}. Total {money(createdQuote.total)}.</p></section>}
      {conversionQuote && <div id="optical-sale-payment"><OpticalSaleConversion quote={conversionQuote} session={session} onCancel={() => setConversionQuote(null)} onSaved={() => { void loadQuotes(); setMessage("Venta graduada registrada con trazabilidad completa."); }} /></div>}
      <section className="sales-card"><h2>Cotizaciones recientes de la sucursal</h2><div className="quotes-list">{quotes.map((item) => {
        const expired = item.validUntil < today();
        return <article key={item.id}><div><strong>{item.folio}</strong><span>{item.customer.fullName}</span></div><div><span>{item.lens.design}</span><strong>{money(item.total)}</strong></div><div><span>Vigencia</span><strong>{item.validUntil}</strong></div><span className={item.status === "ACTIVE" && !expired ? "active-status" : "inactive-status"}>{item.status === "CONVERTED" ? "Convertida" : expired ? "Vencida" : "Activa"}</span><div className="quote-row-actions">{canWrite && item.status === "ACTIVE" && !expired && <button className="primary-button" onClick={() => setConversionQuote(item)} type="button">Convertir en venta</button>}</div></article>;
      })}{quotes.length === 0 && <p className="empty-state">Todavía no hay cotizaciones en esta sucursal.</p>}</div></section>
    </section>
  );
}
